/* Native scrolling remains available without JavaScript. Each rail owns its listeners. */
if (!customElements.get('home-rail')) {
  customElements.define('home-rail', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rail-track]');
      this.controls = this.querySelector('[data-rail-controls]');
      if (!this.track || !this.controls) return;
      this.previous = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.previous.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.track.addEventListener('scroll', () => this.update(), { ...options, passive: true });
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);
      this.update();
    }
    move(direction) {
      const card = this.track.firstElementChild;
      if (!card) return;
      const distance = card.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || 0);
      this.track.scrollBy({ left: direction * distance, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.controls.hidden = max < 2;
      this.previous.disabled = this.track.scrollLeft < 2;
      this.next.disabled = this.track.scrollLeft >= max - 2;
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
    }
  });
}

/* The static product image is the fallback for touch, reduced motion and media errors. */
if (!customElements.get('home-product')) {
  customElements.define('home-product', class extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('.il-product__video');
      if (!this.video) return;
      this.controller = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.pointer = matchMedia('(hover: hover) and (pointer: fine)');
      this.hovered = false;
      this.visible = false;
      this.active = false;
      this.request = 0;
      this.video.muted = true;
      const options = { signal: this.controller.signal };
      this.addEventListener('pointerenter', (event) => {
        this.hovered = event.pointerType !== 'touch';
        this.updatePlayback();
      }, options);
      this.addEventListener('pointerleave', () => {
        this.hovered = false;
        this.updatePlayback();
      }, options);
      this.addEventListener('focusin', () => this.updatePlayback(), options);
      this.addEventListener('focusout', () => queueMicrotask(() => this.updatePlayback()), options);
      this.video.addEventListener('error', () => this.stop(), options);
      this.motion.addEventListener('change', () => this.updatePlayback(), options);
      this.pointer.addEventListener('change', () => this.updatePlayback(), options);
      document.addEventListener('visibilitychange', () => this.updatePlayback(), options);
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.updatePlayback();
      });
      this.observer.observe(this);
    }
    updatePlayback() {
      if (!this.video) return;
      const requested = this.hovered || this.querySelector(':focus-visible');
      if (!this.isConnected || !this.visible || document.hidden || this.motion.matches || !this.pointer.matches || !requested) {
        this.stop();
        return;
      }
      if (this.active) return;
      this.active = true;
      const request = ++this.request;
      this.video.play().then(() => {
        if (this.active && request === this.request) this.classList.add('is-playing');
      }).catch(() => {
        if (request === this.request) this.stop();
      });
    }
    stop() {
      this.active = false;
      this.request++;
      this.classList.remove('is-playing');
      this.video?.pause();
      if (this.video?.readyState) this.video.currentTime = 0;
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.stop();
    }
  });
}

/* Silent science loop; only load/play while visible and respect motion preferences. */
if (!customElements.get('science-video')) {
  customElements.define('science-video', class extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.controller = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.failed = false;
      this.request = 0;
      this.video.muted = true;
      const options = { signal: this.controller.signal };
      this.video.addEventListener('error', () => {
        this.failed = true;
        this.classList.remove('is-playing');
        this.updatePlayback();
      }, options);
      this.motion.addEventListener('change', () => this.updatePlayback(), options);
      document.addEventListener('visibilitychange', () => this.updatePlayback(), options);
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.updatePlayback();
      }, { threshold: 0.1 });
      this.observer.observe(this);
    }
    updatePlayback() {
      const request = ++this.request;
      if (!this.isConnected || !this.visible || document.hidden || this.motion.matches || this.failed) {
        this.video.pause();
        if (this.motion.matches) this.classList.remove('is-playing');
        return;
      }
      this.video.play().then(() => {
        if (request === this.request) this.classList.add('is-playing');
      }).catch(() => {
        if (request !== this.request) return;
        this.failed = true;
        this.classList.remove('is-playing');
        this.updatePlayback();
      });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.request++;
      this.video?.pause();
    }
  });
}

/* A small background-only parallax drift; no continuous animation loop. */
if (!customElements.get('science-background')) {
  customElements.define('science-background', class extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.il-science');
      if (!this.section) return;
      this.controller = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.frame = null;
      const options = { signal: this.controller.signal };
      const measure = () => {
        this.distance = parseFloat(getComputedStyle(this).getPropertyValue('--science-drift')) || 0;
        this.schedule();
      };
      this.motion.addEventListener('change', () => {
        cancelAnimationFrame(this.frame);
        this.frame = null;
        this.style.removeProperty('--science-offset');
        this.schedule();
      }, options);
      window.addEventListener('scroll', () => this.schedule(), { ...options, passive: true });
      window.addEventListener('resize', measure, { ...options, passive: true });
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.schedule();
      });
      this.observer.observe(this.section);
      this.resizeObserver = new ResizeObserver(measure);
      this.resizeObserver.observe(this.section);
      measure();
    }
    schedule() {
      if (!this.visible || this.motion.matches || this.frame !== null) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (!this.isConnected || !this.visible || this.motion.matches) return;
        const rect = this.section.getBoundingClientRect();
        const progress = Math.max(-1, Math.min(1, (innerHeight - rect.height - 2 * rect.top) / (innerHeight + rect.height)));
        this.style.setProperty('--science-offset', `${(progress * this.distance).toFixed(2)}px`);
      });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  });
}
